import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { onSessionExpired } from '@/api/authEvents'
import { authApi } from '@/features/auth/api/authApi'
import { AuthContext, type AuthContextValue } from '@/features/auth/AuthContext'
import type { AuthSession, LoginPayload } from '@/features/auth/types'
import { clearSession, loadSession, saveSession } from '@/utils/tokenStorage'

// SA-5: mensaje exacto que devuelve el backend (app/api/deps.py) cuando la
// organización del usuario está suspendida — deliberadamente genérico,
// nunca revela motivo comercial. Se compara tal cual, no se traduce en
// el backend (ver Sección 4/17 del encargo: mensaje neutro en inglés en
// la API, la traducción/UX vive aquí en el frontend).
const ORGANIZATION_SUSPENDED_MESSAGE = 'Organization access is disabled.'

const ME_QUERY_KEY = ['auth', 'me'] as const

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [session, setSession] = useState<AuthSession | null>(() => loadSession())

  const meQuery = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: authApi.me,
    enabled: session !== null,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  const endSession = useCallback(() => {
    clearSession()
    setSession(null)
    queryClient.removeQueries({ queryKey: ME_QUERY_KEY })
  }, [queryClient])

  useEffect(() => onSessionExpired(endSession), [endSession])

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (tokens) => {
      const next: AuthSession = {
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresAt: Date.now() + tokens.expires_in * 1000,
      }
      saveSession(next)
      setSession(next)
    },
  })

  const login = useCallback(
    async (payload: LoginPayload) => {
      await loginMutation.mutateAsync(payload)
    },
    [loginMutation],
  )

  const logout = useCallback(async () => {
    const current = session
    endSession()
    if (current) {
      await authApi.logout(current.refreshToken).catch(() => {
        // El token ya pudo haber expirado en el servidor; la sesión local ya se limpió.
      })
    }
  }, [session, endSession])

  const isBootstrapping = session !== null && meQuery.isPending

  // SA-5: /auth/me falla con 403 "Organization access is disabled." para
  // un usuario normal cuya organización fue suspendida — get_current_active_user
  // ya lo rechaza ahí mismo (nunca llega a resolver el perfil). Se
  // distingue de cualquier otro 403/401 para no confundirlo con una
  // sesión inválida (no se cierra sesión ni se redirige a /login).
  const isOrganizationSuspended =
    isAxiosError(meQuery.error) &&
    meQuery.error.response?.status === 403 &&
    meQuery.error.response?.data?.detail === ORGANIZATION_SUSPENDED_MESSAGE

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data ?? null,
      isAuthenticated: session !== null && meQuery.data !== undefined,
      isBootstrapping,
      isLoggingIn: loginMutation.isPending,
      isOrganizationSuspended,
      login,
      logout,
    }),
    [session, meQuery.data, isBootstrapping, isOrganizationSuspended, loginMutation.isPending, login, logout],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}
